import React, { useState, useEffect, useMemo, useRef } from 'react';
import ToolCard from '../../../common/element/Card';
import Checkbox from '../../../common/element/Checkbox';
import Button from '../../../common/element/Buttons';
import Alert from '../../../common/element/Alert/Alert';
import SearchInput from '../../../common/element/Search/SearchInput';

const StepComponent = ({
  stepNumber,
  title,
  instruction,
  requiredCount,
  availableCodes = [],
  selectedCodes,
  onSelectionChange,
  onNext,
  onCancel,
  isLastStep = false,
  isReadOnly = false,
  // Step 5 specific props
  isResultStep = false,
  step4NCodes = [],
  resultCCodes = [],
  resultLoading = false,
  resultError = null
}) => {
  const nCodes = availableCodes;
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');
  const [touched, setTouched] = useState(false);
  const [searchText, setSearchText] = useState("");
  
  // Refs for the containers
  const selectedCodesRef = useRef(null);
  const checkboxListRef = useRef(null);

  // Helper functions
  const getDisplayText = (codeItem) => {
    return typeof codeItem === 'object' ? codeItem.text : codeItem;
  };

  const getCodeKey = (codeItem, index) => {
    return typeof codeItem === 'object' ? codeItem.id : index;
  };

  const isCodeSelected = (codeItem) => {
    const itemId = typeof codeItem === 'object' ? codeItem.id : codeItem;
    return selectedCodes.some(selected => {
      const selectedId = typeof selected === 'object' ? selected.id : selected;
      return selectedId === itemId;
    });
  };

  // Auto-scroll function for new selections (only when adding items)
  const scrollToLatestSelected = () => {
    if (selectedCodesRef.current && selectedCodes.length > 0) {
      const container = selectedCodesRef.current;
      // Scroll to the bottom to show the latest selected item
      container.scrollTop = container.scrollHeight;
    }
  };

  // Scroll to top function for step navigation (checkbox list)
  const scrollCheckboxListToTop = () => {
    if (checkboxListRef.current) {
      const container = checkboxListRef.current;
      container.scrollTop = 0;
    }
  };

  // Filter codes based on search text
  const filteredCodes = useMemo(() => {
    const searchValue = typeof searchText === 'string' ? searchText : (searchText?.target?.value || '');
    if (!searchValue || !searchValue.trim()) {
      return nCodes;
    }
    return nCodes.filter(codeItem => {
      const text = getDisplayText(codeItem).toLowerCase();
      return text.includes(searchValue.toLowerCase().trim());
    });
  }, [nCodes, searchText]);

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
  };

  const handleCodeToggle = (codeItem) => {
    if (isReadOnly) return;

    setTouched(true);
    const itemId = typeof codeItem === 'object' ? codeItem.id : codeItem;

    const isSelected = selectedCodes.some(selected => {
      const selectedId = typeof selected === 'object' ? selected.id : selected;
      return selectedId === itemId;
    });

    if (isSelected) {
      const newSelection = selectedCodes.filter(selected => {
        const selectedId = typeof selected === 'object' ? selected.id : selected;
        return selectedId !== itemId;
      });
      onSelectionChange(newSelection);
      setAlertVisible(false);
      return;
    }

    // For steps other than step 1, enforce exact requiredCount cap
    if (stepNumber !== 1 && selectedCodes.length >= requiredCount) {
      setAlertMessage(`You can only select ${requiredCount} items for this step.`);
      setAlertType("danger");
      setAlertVisible(true);
      return;
    }

    const newSelection = [...selectedCodes, codeItem];
    onSelectionChange(newSelection);

    // Auto-scroll to show the newly selected item
    setTimeout(() => {
      scrollToLatestSelected();
    }, 100);

    if (newSelection.length === requiredCount) {
      const message = stepNumber === 1
        ? `You have reached the minimum required ${requiredCount} items. You can select more.`
        : `You have selected the required ${requiredCount} items.`;
      setAlertMessage(message);
      setAlertType("success");
      setAlertVisible(true);
    } else {
      setAlertVisible(false);
    }
  };

  // Reset alert, touched, and search when the user navigates to a different step
  // Also scroll checkbox list to top when step changes
  useEffect(() => {
    setAlertVisible(false);
    setAlertMessage('');
    setAlertType('info');
    setTouched(false);
    setSearchText('');
    
    // Scroll checkbox list to top when step changes
    setTimeout(() => {
      scrollCheckboxListToTop();
    }, 100);
  }, [stepNumber]);

  const canProceed = stepNumber === 5 ? true : selectedCodes.length >= requiredCount;

  // Step 5 specific rendering
  if (isResultStep) {
    return (
      <div className="flex-1 bg-white px-0 md:px-6 lg:px-8">
        <ToolCard className="">
          {/* Header */}
          <div className="rounded-lg p-6 text-start ">
            <h2 className="font-bold text-[18px] md:text-[24px] leading-[115%] text-black font-lato">{instruction}</h2>
          </div>

          {/* Step 5 Content */}
          <div className=" rounded-lg p-4 md:px-6 pb-0">
            {resultLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8E1C]"></div>
                <p className="ml-4 text-gray-600">Loading results...</p>
              </div>
            ) : resultError ? (
              <div className="text-center py-12">
                <p className="text-red-600 mb-4">Error loading results: {resultError}</p>
              </div>
            ) : (
              <div className="rounded-lg overflow-hidden">
                <div className="grid grid-cols-2 mb-[18px]">
                  <div className="text-start font-semibold text-[18px] md:text-[24px] text-[#FF8E1C] font-lato">N Code:</div>
                  <div className="text-end font-semibold text-[18px] md:text-[24px] text-[#FF8E1C] font-lato">E Code:</div>
                </div>
                <div className="bg-white">
                  {resultCCodes.length > 0 ? (
                    <div className="space-y-4">
                      {resultCCodes.map((item, index) => (
                        <div key={item.id || index} className="bg-[#FFF4E5] rounded-lg overflow-hidden">
                          <div className="block md:flex lg:flex justify-between items-center p-[16px] md:p-[12px_24px]">
                            <div className=" w-[100%] md:w-[48%] mb-3 md:mb-0">
                              <p className="font-medium text-[16px] md:text-[18px] lg:text-[18px] text-black font-lato text-start">{item.ncode}</p>
                            </div>
                            <div className='w-[100%] md:w-[4%] mb-3 md:mb-0'>
                            <div className='w-[100%] h-[2px] md:w-px md:h-[36px] bg-[#FF8E1C] shrink-0'></div>
                            </div>
                            <div className=" w-[100%] md:w-[48%] ">
                              <p className="font-medium text-[16px] md:text-[18px] lg:text-[18px] text-black font-lato text-start">{item.ecode}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center">
                      <p className="text-gray-500">No results available</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex justify-end space-x-4 mt-8">
              <Button
                onClick={onCancel}
                className="w-[150px] h-[48px] bg-[#FFF4E5] border border-[0.5px] border-[#FFF4E5] rounded-[12px]  font-lato font-semibold text-[16px] leading-[115%]"
              >
                Back
              </Button>
              <Button
                onClick={onNext}
                disabled={!canProceed}
                className={`w-[150px] h-[48px] rounded-[12px] text-white font-lato font-semibold text-[16px] leading-[115%] ${
                  canProceed ? 'bg-[#FF8E1C]' : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Submit
              </Button>
            </div>

            {!resultLoading && !resultError && resultCCodes.length > 0 && (
              <div className="p-4 bg-[#FF8E1C] rounded-[12px] font-lato font-bold text-[18px] md:text-[24px] text-white mt-8">
                Take a Screenshot & Share Your Result With Your Coach
              </div>
            )}
          </div>
        </ToolCard>
      </div>
    );
  }

  // Steps 1–4
  return (
    <div className="flex-1 bg-white px-0 md:px-6 lg:px-8">
      <ToolCard className="">
        {/* Header */}
        <div className="rounded-lg p-4 md:px-6 pt-[32px] text-start">
          <h2 className="font-bold text-[18px] md:text-[24px] text-black font-lato">{instruction}</h2>

          <div className="mt-4 flex justify-between flex-wrap md:flex-nowrap lg:flex-nowrap items-center gap-2">
            <p className='font-bold text-[16px] md:text-[20px] text-black font-lato mb-0'>
              N Codes Assessment Selected: <span className="font-medium">{selectedCodes.length}</span>
            </p>
            {!isReadOnly && (
              <SearchInput
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Search N Codes Assessment..."
              />
            )}
          </div>

          {touched && alertVisible && (
            <div className="mt-4">
              <Alert
                type={alertType}
                showIcon
                closable
                onClose={() => setAlertVisible(false)}
              >
                {alertMessage}
              </Alert>
            </div>
          )}
        </div>

        {/* Content */}
        <div className=" rounded-lg p-4 md:px-6 pb-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left side - Checkboxes */}
            <div className='xl:col-span-8 lg:col-span-12'>
              {!isReadOnly && searchText && (
                <div className="mb-4 text-sm text-gray-600">
                  Showing {filteredCodes.length} of {nCodes.length} N Codes Assessment
                  {filteredCodes.length === 0 && (
                    <span className="text-[#FF8E1C] ml-2">No matches found</span>
                  )}
                </div>
              )}
              <div 
                ref={checkboxListRef}
                className="space-y-3 max-h-[66vh] overflow-y-auto"
                style={{ scrollBehavior: 'smooth' }}
              >
                {(isReadOnly ? nCodes : filteredCodes).map((codeItem, index) => {
                  const globalIndex = isReadOnly ? index : nCodes.indexOf(codeItem);
                  const displayNumber = (globalIndex !== -1 ? globalIndex : index) + 1;
                  return (
                  <label
                    key={getCodeKey(codeItem, index)}
                    className={`flex rounded-lg items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 border
                      ${isCodeSelected(codeItem) ? 'border-[#FF8E1C]' : 'border-gray-300'}`}
                  >
                    <Checkbox
                      checked={isCodeSelected(codeItem)}
                      onChange={() => handleCodeToggle(codeItem)}
                      value={typeof codeItem === 'object' ? codeItem.id : codeItem}
                      disabled={isReadOnly}
                    />
                    <span className="font-medium text-[16px] md:text-[18px] lg:text-[18px] text-black font-lato text-start">
                      {displayNumber}. {getDisplayText(codeItem)}
                    </span>
                  </label>
                  );
                })}
              </div>
            </div>

            {/* Right side - Selected NCodes */}
            <div className='xl:col-span-4 lg:col-span-12'>
              <h3 className="font-semibold text-[18px] md:text-[24px] text-black font-lato leading-[16px] text-start mb-[18px]">
                Selected N Codes Assessment
              </h3>
              <div 
                ref={selectedCodesRef}
                className="space-y-2 max-h-[62vh] overflow-y-auto"
                style={{ scrollBehavior: 'smooth' }}
              >
                {selectedCodes.length === 0 ? (
                  <p className="text-[16px] md:text-[20px] text-black font-lato h-[300px] flex justify-center items-center">
                    No codes selected yet
                  </p>
                ) : (
                  selectedCodes.map((codeItem, index) => (
                    <div
                      key={getCodeKey(codeItem, index)}
                      className="text-start font-medium text-[16px] md:text-[18px] lg:text-[18px] text-white font-lato p-3 bg-[#FF8E1C] rounded-lg"
                    >
                      {index + 1}. {getDisplayText(codeItem)}
                    </div>
                  ))
                )}
              </div>

              {isReadOnly && selectedCodes.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-start font-medium text-[14px] font-lato text-blue-700">
                    <strong>Note:</strong> These {selectedCodes.length} N-Codes were selected in Step 3.
                    Please review them and click "Next" to proceed to the final step.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-4 my-8">
            <Button
              onClick={onCancel}
              className="w-[150px] h-[48px] bg-[#FFF4E5] border border-[0.5px] border-[#FFF4E5] rounded-[12px] font-lato font-semibold text-[16px] leading-[115%]"
            >
              Back
            </Button>
            <Button
              onClick={onNext}
              disabled={!canProceed}
              className={`w-[150px] h-[48px] rounded-[12px] text-white font-lato font-semibold text-[16px] leading-[115%] ${
                canProceed ? 'bg-[#FF8E1C]' : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {isLastStep ? 'Finish' : 'Next'}
            </Button>
          </div>

          {stepNumber === 4 && (
            <div className="p-4  bg-[#FF8E1C] rounded-[12px] font-lato font-bold text-[18px] md:text-[24px] text-white">
              Click Next To Generate E Codes Against Above N Codes
            </div>
          )}
        </div>
      </ToolCard>
    </div>
  );
};

export default StepComponent;